/** Mobilisation tab for the Customer Workspace (Demo Mode only). Manages the launch plan,
 * tasks, owners, due dates, dependencies, readiness checks, blockers and completion status -
 * see `../../../../mocks/data/customerManagementData.ts`. */

import {
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { CustomerRecord } from '../../../../mocks/data/customerManagementData';

interface MobilisationTabProps {
  customer: CustomerRecord;
}

const STATUS_COLOR: Record<CustomerRecord['mobilisation'][number]['status'], 'success' | 'warning' | 'default' | 'error'> = {
  Complete: 'success',
  'In Progress': 'warning',
  'Not Started': 'default',
  Blocked: 'error',
};

const MobilisationTab = ({ customer }: MobilisationTabProps) => {
  const { t } = useTranslation('common');

  return (
    <Section>
      <TableHeader>
        {t('mobilisationTableTitle', 'Onboarding checklist')}
        <Pill text={`${customer.mobilisation.length} ${t('mobilisationCountLabel', 'tasks')}`} />
      </TableHeader>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('mobilisationTask', 'Task')}</TableCell>
              <TableCell>{t('mobilisationOwner', 'Owner')}</TableCell>
              <TableCell>{t('mobilisationDueDate', 'Due Date')}</TableCell>
              <TableCell>{t('mobilisationDependency', 'Dependency')}</TableCell>
              <TableCell>{t('mobilisationStatus', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customer.mobilisation.length === 0 && (
              <TableRow><TableCell colSpan={5}>{t('mobilisationNoTasks', 'No mobilisation tasks recorded.')}</TableCell></TableRow>
            )}
            {customer.mobilisation.map((task) => (
              <TableRow key={task.task}>
                <TableCell>{task.task}</TableCell>
                <TableCell>{task.owner}</TableCell>
                <TableCell>{task.dueDate}</TableCell>
                <TableCell>{task.dependency ?? '-'}</TableCell>
                <TableCell>
                  <Chip label={task.status}
                    size="small"
                    color={STATUS_COLOR[task.status]}
                    variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default MobilisationTab;
